import create from "zustand";
import { ProductType } from "./paymentTypes";
import { usePaymentCheckoutCourseState } from "./paymentCheckoutCourseState";
import { usePaymentCheckoutAboBasicState } from "./paymentCheckoutAboBasicState";
import { usePaymentCheckoutAboDuoState } from "./paymentCheckoutAboDuoState";
import { usePaymentCheckoutAboFamilyState } from "./paymentCheckoutAboFamilyState";
import { shCheckoutCurrentPriceInformation } from "../../../modals/concrete/checkout/helper/shCheckoutHelper";
import { getAPIUrl } from "../../../helper/constants/uri";
import useAuthState from "../../authState";
import defaultPaymentInfoServiceInstance from "../../../provider/paymentInfoService";
import { shSIBTrackEvent, trackClearECommerce, trackEvent, trackFBEvent } from "../../../helper/tracking/tracking";
import { PhStepTracker } from "../../helper/phStepTracker";
import { GtStepTracker } from "../../helper/gtStepTracker";
import useInfoState from "../../infoState";

declare var window: any;

type StepType = "login"|"register"|"checkout-voucher"|"product-choose"|"pass-choose"|"payment-choose"|"payment"|"success";
type BuyMode = "abo"|"course";

const trackPaymentFB = (eventName: string) => {
    let itemName = null;
    let itemId = null;

    const buyMode = usePaymentCheckoutState.getState().buyMode;
    const priceInformation = shCheckoutCurrentPriceInformation();

    if (buyMode === "abo") {
        const product = usePaymentCheckoutState.getState().currentProduct;

        itemName = product.tag;
        itemId = "P" + product.id;
    } else {
        const course = usePaymentCheckoutCourseState.getState().course;

        if (course) {
            itemName = course.title;
            itemId = "C" + course.id;
        }
    }

    if (itemId === undefined) {
        return;
    }

    trackFBEvent(eventName, {
        content_ids: [ itemId ],
        content_name: [ itemName ],
        currency: 'EUR',
        value: priceInformation.totalPrice
    });
}

const tracker = new PhStepTracker("checkout modal");
const gtStepTracker = new GtStepTracker();

interface PaymentModalState {

    loading: boolean;
    setLoading: (loading: boolean) => void;

    showCheckoutDialog: boolean;
    setShowCheckoutDialog: (showCheckoutDialog) => void;

    step: StepType;
    setStep: (step: StepType) => void;

    errorMessage: string;
    setErrorMessage: ( errorMessage: string ) => void;

    currentProduct: ProductType;
    setCurrentProduct: (currentProduct: ProductType) => void;

    buyMode: BuyMode,
    setBuyMode: (buyMode: BuyMode) => void;

    isGiftcard: boolean;
    setIsGiftcard: (isGiftcard: boolean) => void;


    paymentMethod: string;
    setPaymentMethod: (paymentMethod: string) => void;

    hasWalletPayment: boolean;
    setHasWalletPayment: (hasWalletPayment: boolean) => void;

    trackedSteps: any[];
    clearTrackedSteps: () => void;
    addTracked: (step) => void;
    hasTracked: (step) => boolean;

    reset: () => void;
    recalculate: () => Promise<void>;

    postData: {
        tagOrId: string;
        type: string;
        voucherCode: string;
        buyMode: string;
    },
    recalculatePostData: () => void;

    stripeUrl: string;
    braintreeUrl: string;

    isRedeemPage: boolean;
    setIsRedeemPage: (isRedeemPage: boolean) => void;

    trackedInitiateCheckout: boolean;
    trackPurchase: (tranactionId: string) => void;

    skipPassChoose: boolean;
    setSkipPassChoose: (skipPassChoose: boolean) => void;
}

const usePaymentCheckoutState = create<PaymentModalState>(
    (set, get) => ({

        skipPassChoose: false,
        setSkipPassChoose: (skipPassChoose) => set( { skipPassChoose } ),

        isRedeemPage: false,
        setIsRedeemPage: (isRedeemPage: boolean) => set( { isRedeemPage } ),

        buyMode: "abo",
        setBuyMode: (buyMode) => {
            set( { buyMode } );
            get().recalculatePostData();
        },

        reset: () => {
            set(
                {
                    step: 'login',
                    showCheckoutDialog: false,
                    isGiftcard: false,
                    errorMessage: null,
                    currentProduct: useInfoState.getState().aboBasic
                }
            );

            tracker.reset();

            usePaymentCheckoutCourseState.getState().setCourse(null);
        },

        loading: false,
        setLoading: (loading) => set( {loading} ),

        showCheckoutDialog: false,
        setShowCheckoutDialog: (showCheckoutDialog) => {
            set( { showCheckoutDialog } );

            if (showCheckoutDialog) {
                trackPaymentFB("AddToCart");

                trackClearECommerce();

                const {
                    itemId,
                    itemName,
                    itemCategory,
                    brutto,
                } = getCurrentTrackingItem();

                trackEvent(
                    "add_to_cart",
                    {
                        ecommerce: {
                            items: [
                                {
                                    "item_id": itemId,
                                    "item_list_id": "C001",
                                    "item_list_name": "Courses Landingpage",
                                    "item_name": itemName,
                                    "item_category": itemCategory,
                                    "price": brutto,
                                    "item_price": brutto,
                                    "quantity": 1,
                                    "currency": "EUR"
                                }
                            ]
                        }
                    }
                );


            }
        },

        errorMessage: "",
        setErrorMessage: (errorMessage) => set( { errorMessage } ),

        trackedInitiateCheckout: false,

        step: "register",
        setStep: (step) => {
            set( { step } );
            tracker.track(step);

            const {
                itemId,
                itemName,
                itemCategory,
                voucherCode,
                brutto,
                startPrice,
                discountPrice
            } = getCurrentTrackingItem();

            try {
                gtStepTracker.track(step, itemId, itemName, brutto, get().paymentMethod);
            } catch(err) {

            }

            if (step === "pass-choose" ||
                step === "product-choose") {

                if (! (get().trackedInitiateCheckout) ) {
                    trackPaymentFB("InitiateCheckout");
                    set( { trackedInitiateCheckout: true } );

                    trackClearECommerce();
                    trackEvent(
                        "begin_checkout", {
                            "ecommerce": {
                                "coupon": voucherCode,
                                "payment_type": get().paymentMethod,
                                "items": [
                                    {
                                        "item_id": itemId,
                                        "item_list_id": "C001",
                                        "item_list_name": "Courses Landingpage",
                                        "item_name": itemName,
                                        "item_category": itemCategory,
                                        "price": brutto,
                                        "item_price": brutto,
                                        "quantity": 1,
                                        "currency": "EUR",
                                        "affiliation": "Website",
                                        "index": 0,
                                        "discount": {
                                            "title": itemName,
                                            "startPrice": startPrice,
                                            "totalPrice": brutto,
                                            "discountPrice": discountPrice
                                        }
                                    }
                                ],
                                "value": brutto
                            },
                        }
                    )

                }

            }

            if (step === "payment") {

                const { itemId, itemName, voucherCode, tax, brutto, startPrice, discountPrice } = getCurrentTrackingItem();

                trackEvent(
                    "add_payment_info", {
                        "ecommerce": {
                            "coupon": voucherCode,
                            "payment_type": get().paymentMethod,
                            "items": [
                                {
                                    "item_id": itemId,
                                    "item_list_id": "C001",
                                    "item_list_name": "Courses Landingpage",
                                    "item_name": itemName,
                                    "item_category": itemCategory,
                                    "price": brutto,
                                    "item_price": brutto,
                                    "quantity": 1,
                                    "currency": "EUR",
                                    "affiliation": "Website",
                                    "index": 0,
                                    "discount": {
                                        "title": itemName,
                                        "startPrice": startPrice,
                                        "totalPrice": brutto,
                                        "discountPrice": discountPrice
                                    }
                                }
                            ],
                            "value": brutto
                        }
                    },
                )


            }

        },
        selectedProduct: null,


        isGiftcard: false,
        setIsGiftcard: (isGiftcard) => {
            set( { isGiftcard } );
            get().recalculate();
        } ,


        paymentMethod: "paypal",
        setPaymentMethod: (paymentMethod) => set( { paymentMethod } ),

        currentProduct: null,
        setCurrentProduct: (product) => {
            set( { currentProduct: product });
            get().recalculate();
        },

        hasWalletPayment: false,
        setHasWalletPayment: (hasWalletPayment) => set( { hasWalletPayment } ),

        recalculate: async () => {
            usePaymentCheckoutAboBasicState.getState().calculate();
            usePaymentCheckoutAboDuoState.getState().calculate();
            usePaymentCheckoutAboFamilyState.getState().calculate();
            usePaymentCheckoutCourseState.getState().calculate();

            get().recalculatePostData();
        },

        trackedSteps: [],
        clearTrackedSteps: () => {
            set( { trackedSteps: [] });
        },
        addTracked: (step) => {
            set( state => ( { trackedSteps: [ ...state.trackedSteps, step ] } ) );
        },
        hasTracked: (step) => {
            const trackedSteps = get().trackedSteps;
            return !!trackedSteps.find(s => s == step);
        },

        stripeUrl: null,
        braintreeUrl: null,

        postData: null,
        recalculatePostData: () => {

            const buyMode = get().buyMode;
            const isGiftcard = get().isGiftcard;
            let currentProduct = get().currentProduct;

            // checking if valid
            if (currentProduct?.tag !== "basic" && isGiftcard) {
                set( { isGiftcard: false } );
            }

            let tagOrId = null;

            if (buyMode === "course") {

                tagOrId = usePaymentCheckoutCourseState.getState().course?.id;

                set( {
                    stripeUrl: getAPIUrl() + "/stripe/product",
                    braintreeUrl: getAPIUrl() + "/braintree/product",
                });

            } else if (buyMode === "abo" && isGiftcard) {

                tagOrId = usePaymentCheckoutState.getState().currentProduct?.tag;

                set( {
                    stripeUrl: getAPIUrl() + "/stripe/product",
                    braintreeUrl: getAPIUrl() + "/braintree/product",
                });

            } else {
                tagOrId = get().currentProduct?.tag;

                set( {
                    stripeUrl: getAPIUrl() + "/stripe/subscription",
                    braintreeUrl: getAPIUrl() + "/braintree/subscription"
                });

            }

            const priceInformation = shCheckoutCurrentPriceInformation();

            let voucherCode = priceInformation.voucherCode;

            if (defaultPaymentInfoServiceInstance.voucherCode) {
                voucherCode = defaultPaymentInfoServiceInstance.voucherCode;
            }

            set( {
                postData: {
                    tagOrId,
                    buyMode: get().isGiftcard ? "giftcard" : "normal",
                    type: buyMode,
                    voucherCode
                }
            });
        },

        trackPurchase: (transactionId: string) => {

            const {
                priceInformation,
                netto,
                brutto,
                tax,
                voucherCode,
                itemName,
                itemId,
                itemCategory
            } = getCurrentTrackingItem();

            // global variables
            window.transactionId = transactionId;
            window.netto = netto;

            trackEvent('ecommercePurchase', {
                'ecommerce': {
                    'payment_type': get().paymentMethod,
                    'purchase': {
                        'actionField': {
                            'id': transactionId,
                            'affiliation': 'Online Store',
                            // Total transaction value (incl. tax and shipping) (somit ergo brutto)
                            'revenue': brutto,
                            'tax': tax,
                            'shipping': '0.00',
                            'coupon': voucherCode,
                            'action': 'purchase'
                        },
                        'payment_type': get().paymentMethod,
                        'products': [
                            {
                                'name': itemName,
                                'id': itemId,
                                'price': brutto,
                                'brand': '7Hauben',
                                'category': itemCategory,
                                'variant': '',
                                'quantity': 1,
                                'coupon': ''
                            }
                        ]
                    }
                }
            });

            const voucher = defaultPaymentInfoServiceInstance.voucher;

            trackClearECommerce();
            trackEvent(
                "purchase",
                {
                    ecommerce: {
                        coupon: voucherCode,
                        payment_type: get().paymentMethod,
                        items: [
                            {
                                "item_id": itemId,
                                "item_list_id": "C001",
                                "item_list_name": "Courses Landingpage",
                                "item_name": itemName,
                                "item_category": itemCategory,
                                "price": brutto,
                                "item_price": brutto,
                                "quantity": 1,
                                "affiliation": "Website",
                                "currency": "EUR",
                                "index": 0,
                                "discount": voucher ?
                                    {
                                        "title": voucher.title,
                                        "startPrice": priceInformation.startPrice,
                                        "totalPrice": priceInformation.totalPrice,
                                        "discountPrice": priceInformation.discountPrice,
                                        "voucherStatus": priceInformation.voucherStatus,
                                        "voucherTag": priceInformation.voucherTag
                                    }
                                    : undefined
                            }
                        ],
                        value: netto,
                        tax
                    }
                },
            );


            trackFBEvent('Purchase', {
                content_ids: [ itemId ],
                content_name: itemName,
                currency: 'eur',
                num_items: 1,
                contents: [
                    {
                        id: itemId,
                        quantity: 1,
                        currency: 'eur',
                        title: itemName
                    }
                ],
                value: brutto
            });

            shSIBTrackEvent('order_completed', { 'product': itemName });


        }

    })
);

export default usePaymentCheckoutState;

useAuthState.subscribe(
    (state) => {
        if (state.currentUserOverview) {
            usePaymentCheckoutState.getState().recalculate();
        }
    }
);

const getCurrentTrackingItem = () => {
    const currentUserOverview = useAuthState.getState().currentUserOverview;
    const priceInformation = shCheckoutCurrentPriceInformation();

    const vat = Number.parseFloat( currentUserOverview ? currentUserOverview.vat + "" : "19");
    const tax = Math.round(
        ( (priceInformation.totalPrice / (100.0 + vat) ) * vat) * 100
    ) / 100;

    const netto = priceInformation.totalPrice - tax;

    let itemName = null;
    let itemId = null;
    let itemCategory = null;

    const buyMode = usePaymentCheckoutState.getState().buyMode;
    const isGiftcard = usePaymentCheckoutState.getState().isGiftcard;

    if (buyMode === "abo") {
        const product = usePaymentCheckoutState.getState().currentProduct;

        itemName = product.tag;
        itemId = "P" + product.id;

        if (isGiftcard) {
            itemCategory = "Abo-Gutschein";
        } else {
            itemCategory = "Abo";
        }
    } else {
        const course = usePaymentCheckoutCourseState.getState().course;
        if (course) {
            itemName = course.title;
            itemId = "C" + course.id;
        }

        if (isGiftcard) {
            itemCategory = "Kurs-Gutschein";
        } else {
            itemCategory = "Kurs";
        }
    }

    return {
        netto,
        itemName,
        itemId,
        itemCategory,
        brutto: priceInformation.totalPrice,
        voucherCode: priceInformation.voucherCode,
        discountPrice: priceInformation.discountPrice,
        startPrice: priceInformation.startPrice,
        tax,
        priceInformation
    }
}
